import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";

const AskRyanBadge = ({ refreshKey }) => {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ask-ryan/usage`).then(r => setUsage(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!usage?.is_trial) return null;
  return (
    <span className="ml-auto font-mono text-[8px] text-[#D9C8A6] uppercase" data-testid="ask-ryan-free-badge">
      {usage.remaining > 0 ? `${usage.remaining} free left` : "Upgrade"}
    </span>
  );
};

const FunnelLink = () => {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    axios.get(`${API}/events/access`).then(r => setAllowed(r.data.allowed)).catch(() => {});
  }, []);
  if (!allowed) return null;
  return <Link to="/funnel" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="funnel-sidebar-link">▽ Conversion Funnel</Link>;
};

export const Sidebar = ({
  sidebarOpen, setSidebarOpen, passedUser, isPro, currentPlan, askRyanOpen, coveredByTeam, subscriptionLoading,
  stats, usage, collections, selectedCollection, setSelectedCollection,
  smartSuggestions, setUpgradeModalOpen, setCollectionModalOpen,
  setEditingCollection, setCollectionForm, setAskRyanOpen,
  setSmartGroupsOpen, setExportModalOpen, setSettingsModalOpen,
  handleShareCollection, handleDeleteCollection, logout,
}) => (
  <aside className={`sidebar-dark fixed md:static inset-y-0 left-0 z-40 transform transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col h-full`}>
    {/* Header */}
    <div className="h-[60px] border-b border-[rgba(232,160,48,0.1)] flex items-center justify-between px-4">
      <div className="flex items-center gap-3">
        <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45"></div>
        <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
      </div>
      <button onClick={() => setSidebarOpen(false)} className="md:hidden text-[#8C7A5A] hover:text-[#E8A030]">✕</button>
    </div>

    {/* User Info */}
    <div className="p-4 border-b border-[rgba(232,160,48,0.1)]">
      <div className="flex items-center gap-3">
        {passedUser?.picture ? (
          <img src={passedUser.picture} alt={passedUser.name} className="w-10 h-10 rounded-full" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[rgba(232,160,48,0.1)] flex items-center justify-center">
            <span className="text-[#E8A030] font-bold">{passedUser?.name?.charAt(0) || "U"}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-[#FDF8EE] truncate flex items-center gap-1.5">
            {passedUser?.name}
            {isPro && <span className="text-[#E8A030]">◈</span>}
          </p>
          <p className="text-[11px] text-[#4A3A1A] truncate font-mono">{passedUser?.email}</p>
        </div>
      </div>
    </div>

    {/* Upgrade CTA / Active badge */}
    {subscriptionLoading ? (
      <div className="px-4 py-3 border-b border-[rgba(232,160,48,0.1)] h-[68px]" data-testid="plan-loading" />
    ) : !isPro ? (
      <div className="px-4 py-3 border-b border-[rgba(232,160,48,0.1)]">
        <button onClick={() => setUpgradeModalOpen(true)} className="w-full p-3 bg-[rgba(232,160,48,0.04)] border border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] transition-colors text-left" data-testid="upgrade-cta-btn">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#E8A030]">◈</span>
            <span className="text-sm font-semibold text-[#FDF8EE]">Upgrade to Signal</span>
          </div>
          <p className="text-[10px] text-[#4A3A1A] font-mono">Unlimited entries & volumes, 50 Ask Ryan queries/mo</p>
        </button>
      </div>
    ) : (
      <div className="px-4 py-3 border-b border-[rgba(232,160,48,0.1)]">
        <div className="flex items-center gap-2 p-2 bg-[rgba(232,160,48,0.04)]">
          <span className="text-[#E8A030]">◈</span>
          <span className="text-sm font-medium text-[#E8A030]" data-testid="plan-active-badge">{currentPlan?.includes('synthesis') || currentPlan?.includes('enterprise') || currentPlan?.includes('team') ? 'Synthesis' : 'Signal'} Active</span>
        </div>
        {coveredByTeam && (
          <p className="font-mono text-[10px] text-[#D9C8A6] mt-1.5 px-2" data-testid="covered-by-team-note">Covered by {coveredByTeam}</p>
        )}
      </div>
    )}

    {/* Stats */}
    <div className="p-4 border-b border-[rgba(232,160,48,0.1)]">
      <div className="grid grid-cols-2 gap-3">
        <div className="stat-card-dark">
          <p className="text-2xl font-bold text-[#E8A030] font-serif">
            {stats.total_bookmarks}
            {usage && !usage.bookmarks.unlimited && (
              <span className="text-xs font-normal text-[#4A3A1A] font-mono">/{usage.bookmarks.limit}</span>
            )}
          </p>
          <p className="text-[9px] text-[#4A3A1A] font-mono uppercase tracking-[1px]">Bookmarks</p>
        </div>
        <div className="stat-card-dark">
          <p className="text-2xl font-bold text-[#7AB8A0] font-serif">
            {stats.total_collections}
            {usage && !usage.collections.unlimited && (
              <span className="text-xs font-normal text-[#4A3A1A] font-mono">/{usage.collections.limit}</span>
            )}
          </p>
          <p className="text-[9px] text-[#4A3A1A] font-mono uppercase tracking-[1px]">Volumes</p>
        </div>
      </div>
      {usage && !usage.bookmarks.unlimited && usage.bookmarks.remaining <= 3 && usage.bookmarks.remaining > 0 && (
        <div className="mt-3 p-2 bg-[rgba(200,96,10,0.1)] border border-[rgba(200,96,10,0.2)]">
          <p className="text-[10px] text-[#C8600A]">
            {usage.bookmarks.remaining} bookmark{usage.bookmarks.remaining !== 1 ? 's' : ''} remaining.
            <button onClick={() => setUpgradeModalOpen(true)} className="font-medium underline ml-1">Upgrade</button>
          </p>
        </div>
      )}
    </div>

    {/* Collections */}
    <div className="flex-1 overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-mono text-[8px] uppercase tracking-[2px] text-[#4A3A1A]">Volumes</span>
        <button
          onClick={() => {
            setEditingCollection(null);
            setCollectionForm({ name: "", description: "", color: "#E8A030" });
            setCollectionModalOpen(true);
          }}
          className="w-6 h-6 flex items-center justify-center hover:bg-[rgba(232,160,48,0.1)] text-[#8C7A5A] hover:text-[#E8A030] transition-colors"
          data-testid="add-collection-btn"
        >+</button>
      </div>

      <ScrollArea className="flex-1 px-2">
        <button
          onClick={() => setSelectedCollection(null)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors mb-1 ${selectedCollection === null ? 'bg-[rgba(232,160,48,0.1)] text-[#E8A030]' : 'hover:bg-[rgba(232,160,48,0.04)] text-[#8C7A5A]'}`}
          data-testid="all-bookmarks-btn"
        >
          <span>◈</span>
          <span className="text-sm flex-1">All Bookmarks</span>
          <span className="font-mono text-[10px] text-[#4A3A1A]">{stats.total_bookmarks}</span>
        </button>

        {collections.map((col) => (
          <div key={col.collection_id} className={`group flex items-center gap-3 px-3 py-2.5 transition-colors mb-1 ${selectedCollection === col.collection_id ? 'bg-[rgba(232,160,48,0.1)]' : 'hover:bg-[rgba(232,160,48,0.04)]'}`}>
            <button onClick={() => setSelectedCollection(col.collection_id)} className="flex items-center gap-3 flex-1 text-left" data-testid={`collection-${col.collection_id}`}>
              <span style={{ color: col.color }}>◈</span>
              <span className={`text-sm flex-1 truncate ${selectedCollection === col.collection_id ? 'text-[#FDF8EE]' : 'text-[#8C7A5A]'}`}>{col.name}</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[rgba(232,160,48,0.1)] text-[#4A3A1A] hover:text-[#E8A030] transition-all" data-testid={`volume-menu-${col.collection_id}`}>⋮</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1C1004] border-[rgba(232,160,48,0.15)] text-[#F5ECD0]">
                <DropdownMenuItem onClick={() => { setEditingCollection(col); setCollectionForm({ name: col.name, description: col.description || "", color: col.color }); setCollectionModalOpen(true); }} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]" data-testid={`volume-edit-${col.collection_id}`}>Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShareCollection(col.collection_id)} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]" data-testid={`share-collection-${col.collection_id}`}>Share</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[rgba(232,160,48,0.1)]" />
                <DropdownMenuItem onClick={() => handleDeleteCollection(col.collection_id)} className="text-red-400 hover:text-red-300 hover:bg-[rgba(255,0,0,0.1)]" data-testid={`volume-delete-${col.collection_id}`}>Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </ScrollArea>
    </div>

    {/* Footer */}
    <div className="p-4 border-t border-[rgba(232,160,48,0.1)] space-y-2">
      <button onClick={() => setAskRyanOpen(true)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#E8A030] hover:bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)] hover:border-[rgba(232,160,48,0.3)] transition-all" data-testid="ask-ryan-btn">
        <span className="font-serif font-bold">R</span><span>Ask Ryan</span>
        {!isPro && <AskRyanBadge refreshKey={askRyanOpen} />}
      </button>
      {smartSuggestions.length > 0 && (
        <button onClick={() => setSmartGroupsOpen(true)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#7AB8A0] hover:bg-[rgba(122,184,160,0.08)] border border-[rgba(122,184,160,0.15)] hover:border-[rgba(122,184,160,0.3)] transition-all" data-testid="smart-groups-btn">
          <span>◎</span><span>Smart Groups</span>
          <span className="ml-auto font-mono text-[8px] bg-[rgba(122,184,160,0.15)] text-[#7AB8A0] px-1.5 py-0.5">{smartSuggestions.length}</span>
        </button>
      )}
      <button onClick={() => setExportModalOpen(true)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="export-btn">↓ Export Bookmarks</button>
      <Link to="/analytics" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="analytics-sidebar-link">▤ Analytics</Link>
      <FunnelLink />
      <button onClick={() => setSettingsModalOpen(true)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="settings-btn">⚙ Settings</button>
      {!coveredByTeam && currentPlan && (currentPlan.includes('enterprise') || currentPlan.includes('team') || currentPlan.includes('synthesis')) && (
        <Link to="/admin" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="admin-link">&#9881; Team Admin</Link>
      )}
      <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.04)] transition-colors" data-testid="logout-btn">→ Sign Out</button>
    </div>
  </aside>
);
